import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Alert, Box, Button, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { Action } from "../../types/toolbar";
import CustomModal from "../../components/ui/modal/modal.component";
import ExternalServiceForm from "../../components/service/externalServiceForm.component";
import { useCRUD } from "../../api/crud";
import { ERROR_SNACKBAR, EXTERNAL_SERVICES_ENDPOINT, SUCCESS_SNACKBAR } from "../../consts";
import { useHasPermission } from "../../contexts/auth";
import { ExternalService } from "../../types/externalServices";

export const ExternalServiceDetailsActions = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, error, get, remove } = useCRUD(EXTERNAL_SERVICES_ENDPOINT);
  const [updateOpened, setUpdateOpened] = useState(false);
  const [deleteOpened, setDeleteOpened] = useState(false);
  const [jobsCount, setJobsCount] = useState<number | null>(null);
  const canChange = useHasPermission("service.change_externalservice");
  const canDelete = useHasPermission("service.delete_externalservice");

  // Whether deleting removes the service or only deactivates it depends on
  // whether it has jobs - asked when the dialog opens, so it's current.
  useEffect(() => {
    if (!deleteOpened || !id) return;
    setJobsCount(null);
    get(id).then((response) => {
      const data = response?.data as ExternalService | undefined;
      setJobsCount(data?.jobs_count ?? 0);
    });
  }, [deleteOpened, id, get]);

  const handleDelete = async () => {
    if (loading || !id) return;
    const result = await remove({ id });
    if (result?.success) {
      enqueueSnackbar(
        jobsCount ? t("External service deactivated") : t("External service deleted successfully"),
        SUCCESS_SNACKBAR
      );
      setDeleteOpened(false);
      navigate("/service/external-services");
    } else {
      enqueueSnackbar(t("Failed to delete external service"), ERROR_SNACKBAR);
    }
  };

  const actions: Action[] = [
    {
      icon: <EditIcon />,
      label: t("Update external service"),
      onClick: () => setUpdateOpened(true),
      disabled: !canChange,
    },
    {
      icon: <DeleteIcon />,
      label: t("Delete external service"),
      onClick: () => setDeleteOpened(true),
      color: "var(--errorColor)",
      disabled: !canDelete,
    },
  ];

  const errorMessage = typeof error === "string" ? error : error?.message ?? error?.detail ?? "";

  const deleteModalContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {jobsCount === null ? (
        <Typography variant="body2" color="text.secondary">
          {t("Loading...")}
        </Typography>
      ) : jobsCount > 0 ? (
        <Alert severity="warning">
          {t(
            "This service has jobs ({{count}}), so it will be deactivated instead: it stays on those jobs, but can no longer be chosen for new ones.",
            { count: jobsCount }
          )}
        </Alert>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t("The service has no jobs, so it will be removed permanently together with its areas.")}
        </Typography>
      )}

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Button variant="contained" color="error" onClick={handleDelete} disabled={loading || jobsCount === null}>
        {loading ? t("Processing...") : t("Confirm deletion")}
      </Button>
    </Box>
  );

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={updateOpened}
        setOpened={setUpdateOpened}
        title={t("Update external service")}
        width="min(960px, 96vw)"
        content={
          <ExternalServiceForm
            id={id}
            onSuccess={() => {
              setUpdateOpened(false);
              window.dispatchEvent(new Event("externalServiceUpdated"));
            }}
          />
        }
      />
      <CustomModal
        opened={deleteOpened}
        setOpened={setDeleteOpened}
        title={t("Delete external service")}
        content={deleteModalContent}
      />
    </>
  );
};
